/**
 * Date Comparison Utilities
 * Functions for comparing dates
 */

import dayjs, { type Dayjs } from 'dayjs';
import isSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter';
import isSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore';

// Type for dayjs input - matches what dayjs() constructor accepts
type DayjsInput = string | number | Date | Dayjs | null | undefined;

dayjs.extend(isSameOrBeforePlugin);
dayjs.extend(isSameOrAfterPlugin);

/**
 * Check if date1 is before date2
 * Replaces date1.getTime() < date2.getTime() comparisons
 */
export function isBefore(date1: DayjsInput, date2: DayjsInput): boolean {
  return dayjs(date1).isBefore(dayjs(date2));
}

/**
 * Check if date1 is after date2
 * Replaces date1.getTime() > date2.getTime() comparisons
 */
export function isAfter(date1: DayjsInput, date2: DayjsInput): boolean {
  return dayjs(date1).isAfter(dayjs(date2));
}

/**
 * Check if date1 is the same as date2
 * Optionally specify the unit of comparison (e.g., 'day', 'hour', 'minute')
 */
export function isSame(
  date1: DayjsInput,
  date2: DayjsInput,
  unit?: 'day' | 'hour' | 'minute' | 'second' | 'millisecond',
): boolean {
  return dayjs(date1).isSame(dayjs(date2), unit);
}

/**
 * Check if date1 is the same or before date2
 */
export function isSameOrBefore(date1: DayjsInput, date2: DayjsInput): boolean {
  return dayjs(date1).isSameOrBefore(dayjs(date2));
}

/**
 * Check if date1 is the same or after date2
 */
export function isSameOrAfter(date1: DayjsInput, date2: DayjsInput): boolean {
  return dayjs(date1).isSameOrAfter(dayjs(date2));
}

/**
 * Check if a date is valid
 */
export function isValid(date: DayjsInput): boolean {
  return dayjs(date).isValid();
}
